import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
// Params are provided from Rails via ReactOnRails props
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import Button from '../../../../ui/canon/Button.jsx';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PROGRAM_QUERY = gql`
  query SprintProgram($slug: String) {
    sprintProgram(slug: $slug) { 
      id 
      title 
      slug 
      duration_days 
      is_group 
      price_cents 
      coachProfile { id name }
      cohorts { id starts_on capacity visibility }
    }
  }
`;

const ENROLL_MUTATION = gql`
  mutation EnrollInProgram($sprint_program_id: ID!, $start_on: ISO8601Date!, $cohort_id: ID) {
    enroll_in_sprint_program(sprint_program_id: $sprint_program_id, start_on: $start_on, cohort_id: $cohort_id) {
      enrollment { id sprint_id }
    }
  }
`;

export default function ProgramEnrollmentContainer(props) {
  const { slug } = props || {};
  const { data, loading, error } = useQuery(PROGRAM_QUERY, { variables: { slug } });
  const [enroll, { loading: enrolling }] = useMutation(ENROLL_MUTATION);
  const program = data?.sprintProgram;

  // Steps: 'date' | 'confirm' | 'complete'
  const [step, setStep] = useState('date');
  const [startOn, setStartOn] = useState('');
  const [selectedCohortId, setSelectedCohortId] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState(null);

  const cohorts = useMemo(() => {
    const list = (program?.cohorts || []).filter((c) => c.visibility !== 'private');
    // sort by starts_on ascending
    return list.sort((a, b) => new Date(a.starts_on) - new Date(b.starts_on));
  }, [program]);

  const effectiveStartOn = useMemo(() => {
    if (program?.is_group && selectedCohortId) {
      const cohort = cohorts.find((c) => String(c.id) === String(selectedCohortId));
      return cohort?.starts_on || null;
    }
    return startOn;
  }, [program, selectedCohortId, cohorts, startOn]);

  useEffect(() => {
    if (step === 'complete' && enrollmentId) {
      const t = setTimeout(() => {
        window.location.assign(`/sprints/enrollments/${enrollmentId}`);
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [step, enrollmentId]);

  const onContinue = () => {
    if (program?.is_group) {
      if (selectedCohortId) setStep('confirm');
    } else if (effectiveStartOn) {
      setStep('confirm');
    }
    try {
      if (window.ahoy && typeof window.ahoy.track === 'function') {
        window.ahoy.track('enroll_continue_click', {
          slug: program?.slug,
          program_id: program?.id,
          path: program?.is_group ? 'cohort' : '1_1',
          start_on: effectiveStartOn,
          cohort_id: program?.is_group ? selectedCohortId : null,
        });
      }
    } catch (_) {}
  };

  const onConfirm = async () => {
    const res = await enroll({ variables: { sprint_program_id: program.id, start_on: effectiveStartOn, cohort_id: program.is_group ? selectedCohortId : null } });
    const id = res?.data?.enroll_in_sprint_program?.enrollment?.id;
    if (id) {
      setEnrollmentId(id);
      setStep('complete');
    }
    try {
      if (window.ahoy && typeof window.ahoy.track === 'function') {
        window.ahoy.track('enroll_confirm_click', {
          slug: program?.slug,
          program_id: program?.id,
          path: program?.is_group ? 'cohort' : '1_1',
          start_on: effectiveStartOn,
          cohort_id: program?.is_group ? selectedCohortId : null,
        });
      }
    } catch (_) {}
  };

  const price = program?.price_cents ? `$${(program.price_cents / 100).toLocaleString()}` : 'Free';
  const stepIndex = step === 'date' ? 0 : step === 'confirm' ? 1 : 2;

  if (loading) return <SingleColumnTemplate><div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 text-succession-text-primary">Loading...</div></SingleColumnTemplate>;
  if (error || !program) return <SingleColumnTemplate><div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 text-succession-text-primary">Program not found</div></SingleColumnTemplate>;

  return (
    <SingleColumnTemplate
      title={`Enroll in ${program.title}`}
      subtitle="Complete the steps below to get started"
      variant="wide"
    >
      <div className="mb-4">
        <a href={`/sprints/programs/${program.slug}`} className="inline-flex items-center gap-2 text-sm text-succession-text-secondary hover:text-succession-primary focus:outline-none focus:ring-2 focus:ring-succession-focus-ring">
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Program
        </a>
      </div>
      {/* Stepper */}
      <div className="flex items-center justify-between mb-8">
        {['Choose Start Date', 'Confirm Details', 'Complete'].map((label, idx) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-3">
              <span
                className={`text-sm font-semibold ${
                  idx <= stepIndex ? 'text-succession-text-primary' : 'text-succession-text-muted'
                }`}
              >
                {idx < stepIndex ? (
                  <CheckCircleIcon className="w-5 h-5 text-succession-success" />
                ) : (
                  idx + 1
                )}
              </span>
              <span className={`text-sm font-medium hidden sm:inline ${
                idx <= stepIndex ? 'text-succession-text-primary' : 'text-succession-text-muted'
              }`}>
                {label}
              </span>
            </div>
            {idx < 2 && (
              <div className={`flex-1 h-1 mx-4 rounded ${
                idx < stepIndex ? 'bg-succession-primary' : 'bg-succession-border'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      {step === 'date' && (
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-8 space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-succession-text-primary">Choose Your Start Date</h2>
            <p className="text-succession-text-secondary">
              {program.is_group
                ? <>Select a cohort start date below. The program runs for {program.duration_days} days.</>
                : <>Pick a date to begin your {program.duration_days}-day program. You can start anytime.</>}
            </p>
          </div>

          {program.is_group ? (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-succession-text-primary">Available Cohorts</label>
              {cohorts.length === 0 && <div className="text-succession-text-secondary text-sm">No cohorts available right now.</div>}
              {cohorts.map((c) => {
                const selected = String(selectedCohortId) === String(c.id);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setSelectedCohortId(c.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-succession-focus-ring ${
                      selected
                        ? 'border-succession-primary bg-succession-primary-10 ring-2 ring-succession-primary-20'
                        : 'border-succession-border bg-succession-bg-primary hover:border-succession-primary hover:bg-succession-hover-bg'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1 inline-block h-4 w-4 rounded-full border-2 ${
                          selected ? 'bg-succession-primary border-succession-primary' : 'border-succession-border'
                        }`}
                        aria-hidden="true"
                      />
                      <div>
                        <div className="font-semibold text-succession-text-primary">{new Date(c.starts_on).toLocaleDateString()}</div>
                        <div className="text-sm text-succession-text-secondary">
                          {c.capacity ? `Capacity ${c.capacity}` : 'Open spots'}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="start-date" className="block text-sm font-medium text-succession-text-primary">Start Date</label>
              <input
                id="start-date"
                type="date"
                value={startOn}
                onChange={(e) => setStartOn(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                placeholder="yyyy-mm-dd"
                className="w-full rounded-lg border border-succession-border bg-succession-bg-primary px-3 py-2 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-focus-ring"
              />
            </div>
          )}

          <div className="pt-2">
            <Button
              onClick={onContinue}
              disabled={program.is_group ? !selectedCohortId : !effectiveStartOn}
              variant="primary"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-8 space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-succession-text-primary">Confirm Your Enrollment</h2>
            <p className="text-succession-text-secondary">Review the details below</p>
          </div>
          <div className="space-y-4 p-4 bg-succession-bg-secondary rounded-lg">
            <div className="flex justify-between">
              <span className="text-sm text-succession-text-secondary">Program</span>
              <span className="font-medium text-succession-text-primary">{program.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-succession-text-secondary">Coach</span>
              <span className="font-medium text-succession-text-primary">{program.coachProfile?.name || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-succession-text-secondary">Start Date</span>
              <span className="font-medium text-succession-text-primary">{effectiveStartOn ? new Date(effectiveStartOn).toLocaleDateString() : '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-succession-text-secondary">Duration</span>
              <span className="font-medium text-succession-text-primary">{program.duration_days} days</span>
            </div>
            <div className="flex justify-between pt-4 border-t border-succession-border">
              <span className="font-semibold text-succession-text-primary">Total</span>
              <span className="font-bold text-lg text-succession-text-primary">{price}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setStep('date')}
              variant="outline"
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={onConfirm}
              disabled={enrolling}
              variant="primary"
              size="lg"
              className="flex-1"
            >
              {program.is_group ? 'Join Cohort' : 'Confirm & Enroll'}
            </Button>
          </div>
        </div>
      )}

      {step === 'complete' && (
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-8">
          <div className="rounded-xl border border-succession-border bg-succession-bg-primary p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-succession-success-bg rounded-full flex items-center justify-center mx-auto">
              <CheckCircleIcon className="w-10 h-10 text-succession-success" />
            </div>
            <h2 className="text-2xl font-bold text-succession-text-primary">Enrollment Complete!</h2>
            <p className="text-sm text-succession-text-secondary mb-1">
              Welcome to {program.title}. Your sprint has been created and tasks are ready.
            </p>
            <p className="text-sm text-succession-text-secondary">Redirecting to your workspace...</p>
          </div>
        </div>
      )}
    </SingleColumnTemplate>
  );
}


